<template>
  <transition name="fade">
    <div v-if="isActive" class="overlay" @click="closeOverlay">
      <svg class="overlay-svg">
        <defs>
          <mask id="overlay-mask">
            <rect width="100%" height="100%" fill="white"/>
            <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="10" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
            </filter>
            <template v-for="(area, index) in highlightAreas" :key="index">
              <circle
                  v-if="area.shape === 'circle'"
                  :cx="area.left"
                  :cy="area.top"
                  :r="area.radius"
                  fill="black"
                  filter="url(#softGlow)"
              />
              <rect
                  v-else
                  :x="area.left - area.width / 2"
                  :y="area.top - area.height / 2"
                  :width="area.width"
                  :height="area.height"
                  fill="black"
                  filter="url(#softGlow)"
              />
            </template>
          </mask>
        </defs>
        <rect width="100%" height="100%" :fill="overlayColor" mask="url(#overlay-mask)"/>
      </svg>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useTheme } from 'vuetify';

const props = defineProps({
  selections: Array // Array of objects with { selector: string, shape: 'circle' | 'box' }
});

const theme = useTheme();
const highlightAreas = ref([]);
const isActive = ref(false);

const updateHighlightAreas = async () => {
  await nextTick();
  highlightAreas.value = props.selections.map(selection => {
    const element = document.querySelector(selection.selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const extraSize = selection.shape === 'box' ? 1.33 : 0.665 // 33% larger box or circle
      const width = rect.width * extraSize;
      const height = rect.height * extraSize;
      const radius = Math.max(rect.width, rect.height) * extraSize;
      return {
        top: rect.top + window.scrollY + rect.height / 2,
        left: rect.left + window.scrollX + rect.width / 2,
        width,
        height,
        radius,
        shape: selection.shape
      };
    }
    return null;
  }).filter(Boolean);
  isActive.value = highlightAreas.value.length > 0;
};

watch(() => props.selections, updateHighlightAreas, { deep: true });

onMounted(() => {
  updateHighlightAreas();
  window.addEventListener('resize', updateHighlightAreas);
  window.addEventListener('scroll', updateHighlightAreas);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateHighlightAreas);
  window.removeEventListener('scroll', updateHighlightAreas);
});

const closeOverlay = () => {
  isActive.value = false;
};

const overlayColor = computed(() => {
  return theme.global.name.value === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.7)';
});
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: auto;
  z-index: 2000;
}

.overlay-svg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
